import { Link } from "react-router-dom";
import { useApp } from "@/context/AppContext";
import { MotionSection } from "@/components/FadeIn";
import { FullMouthScroll } from "@/components/FullMouthScroll";
import { ASSETS, DOCTORS, FAQS, TESTIMONIALS, postEvent } from "@/lib/site";
import { Seo, SectionTag, CtaLink, SimDisclaimer } from "@/components/common";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Camera, Stethoscope, ClipboardList, Plane, ShieldCheck, Globe2, Languages, HeartHandshake, Sparkles } from "lucide-react";

export default function Home() {
  const { t } = useApp();

  const trust = [
    { icon: ShieldCheck, text: { tr: "Ruhsatlı sağlık kuruluşu", en: "Licensed healthcare facility" } },
    { icon: Globe2, text: { tr: "Uluslararası hasta koordinasyonu", en: "International patient coordination" } },
    { icon: Languages, text: { tr: "Tercüme desteği", en: "Translation support" } },
    { icon: Plane, text: { tr: "Havaalanı karşılama", en: "Airport pickup" } },
    { icon: HeartHandshake, text: { tr: "Tedavi sonrası takip", en: "Aftercare follow-up" } },
  ];

  const steps = [
    { icon: Camera, title: { tr: "Fotoğraf gönderin", en: "Send your photos" }, desc: { tr: "Gülüşünüzün fotoğraflarını güvenli kanaldan iletin.", en: "Share photos of your smile through a secure channel." } },
    { icon: Stethoscope, title: { tr: "Hekim değerlendirmesi", en: "Dentist evaluation" }, desc: { tr: "Hekimlerimiz uygunluğunuzu klinik olarak değerlendirir.", en: "Our dentists clinically evaluate your suitability." } },
    { icon: ClipboardList, title: { tr: "Kişiye özel plan", en: "Personalised plan" }, desc: { tr: "Size özel tedavi planı ve teklif hazırlanır.", en: "A personal treatment plan and offer are prepared." } },
    { icon: Plane, title: { tr: "Tedavi ve dönüş", en: "Treatment and return" }, desc: { tr: "İstanbul'da tedavi, ülkenizde takip.", en: "Treatment in Istanbul, follow-up at home." } },
  ];

  const layers = [
    { n: "01", title: { tr: "İç yapı", en: "Inner core" }, desc: { tr: "Dayanıklılığı sağlayan yüksek mukavemetli zirkonyum çekirdek.", en: "A high-strength zirconium core that provides durability." } },
    { n: "02", title: { tr: "Doğal gövde", en: "Natural body" }, desc: { tr: "Dişin kendi renk geçişlerini taklit eden gövde katmanı.", en: "A body layer that mimics the tooth's own colour transitions." } },
    { n: "03", title: { tr: "Yarı geçirgen uç", en: "Translucent edge" }, desc: { tr: "Işığı doğal mine gibi geçiren kesici kenar.", en: "An incisal edge that transmits light like natural enamel." } },
  ];

  return (
    <main data-testid="home-page">
      <Seo title={t({ tr: "Dijital Gülüş Tasarımı, İstanbul", en: "Digital Smile Design, Istanbul" })} description={t({ tr: "İstanbul'da dijital gülüş tasarımı ve kişiye özel zirkonyum tedavisi. Uluslararası hasta koordinasyonu.", en: "Digital smile design and personalised zirconium treatment in Istanbul. International patient coordination." })} />

      {/* BRAND HERO + FULL-MOUTH SCROLL EXPERIENCE */}
      <FullMouthScroll />

      {/* TRUST STRIP */}
      <MotionSection className="bg-[#0A0E14] border-y border-white/10" data-testid="trust-strip">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-6 grid grid-cols-2 md:grid-cols-5 md:divide-x md:divide-white/10">
          {trust.map((it, i) => (
            <div key={i} className="flex items-center gap-2.5">
              <it.icon size={18} className="text-[var(--accent)] shrink-0" />
              <span className="text-xs md:text-sm font-medium text-[#F2F5F7]/80">{t(it.text)}</span>
            </div>
          ))}
        </div>
      </MotionSection>

      {/* HOW IT WORKS */}
      <MotionSection className="max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-28" data-testid="how-it-works">
        <SectionTag>{t({ tr: "Nasıl çalışır", en: "How it works" })}</SectionTag>
        <h2 className="font-heading text-2xl md:text-3xl font-semibold text-[#F2F5F7] tracking-tight max-w-xl">
          {t({ tr: "Dört adımda, planlı ve şeffaf bir süreç", en: "A planned, transparent process in four steps" })}
        </h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 mt-12">
          {steps.map((s, i) => (
            <div key={i} className="rounded-xl glass-card p-7 hover:shadow-lg hover:-translate-y-1 transition-transform duration-300">
              <div className="w-11 h-11 rounded-[10px] bg-[var(--accent-soft)] flex items-center justify-center mb-5">
                <s.icon size={20} className="text-[var(--accent)]" />
              </div>
              <span className="text-xs font-bold text-[var(--accent)]">0{i + 1}</span>
              <h3 className="font-heading font-semibold text-[#F2F5F7] mt-1">{t(s.title)}</h3>
              <p className="text-sm text-[#94A3AE] mt-2 leading-relaxed">{t(s.desc)}</p>
            </div>
          ))}
        </div>
      </MotionSection>

      <div className="mirror-divider max-w-6xl mx-auto" aria-hidden="true" />

      {/* ZIRCONIUM */}
      <MotionSection className="bg-[#0A0E14] text-white" data-testid="zirconium-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-28 grid lg:grid-cols-2 gap-14">
          <div>
            <SectionTag>{t({ tr: "Zirkonyum gülüş tasarımı", en: "Zirconium smile design" })}</SectionTag>
            <h2 className="font-heading text-2xl md:text-3xl font-semibold tracking-tight">
              {t({ tr: "Üç katmanlı yapı, doğal form prensipleri", en: "Three-layer structure, natural form principles" })}
            </h2>
            <p className="mt-5 text-white/70 leading-relaxed max-w-md">
              {t({
                tr: "Piyano tuşu gibi dişler ve aşırı beyazlık üretmiyoruz. Sekiz üst ön diş ayrı ayrı planlanır; hedef, size ait görünen bir gülüştür.",
                en: "We do not produce piano-key teeth or extreme whiteness. Each of the eight upper front teeth is planned individually; the goal is a smile that looks like your own.",
              })}
            </p>
            <div className="mt-8">
              <CtaLink to="/tedaviler/zirkonyum" testId="zirconium-more-cta">
                {t({ tr: "Zirkonyum yaklaşımımız", en: "Our zirconium approach" })}
              </CtaLink>
            </div>
          </div>
          <div className="space-y-4">
            {layers.map((l) => (
              <div key={l.n} className="rounded-xl bg-white/5 border border-white/10 p-6 flex gap-5 hover:bg-white/10 transition-colors">
                <span className="font-heading text-2xl font-bold text-[var(--accent-2)]">{l.n}</span>
                <div>
                  <h3 className="font-heading font-semibold">{t(l.title)}</h3>
                  <p className="text-sm text-white/60 mt-1 leading-relaxed">{t(l.desc)}</p>
                </div>
              </div>
            ))}
            <SimDisclaimer />
          </div>
        </div>
      </MotionSection>

      {/* DOCTORS */}
      <MotionSection className="max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-28" data-testid="doctors-section">
        <div className="flex items-end justify-between flex-wrap gap-4">
          <div>
            <SectionTag>{t({ tr: "Hekim kadrosu", en: "Our doctors" })}</SectionTag>
            <h2 className="font-heading text-2xl md:text-3xl font-semibold text-[#F2F5F7] tracking-tight">
              {t({ tr: "Tedavinizi planlayan hekimler", en: "The dentists planning your treatment" })}
            </h2>
          </div>
          <Link to="/hekimler" data-testid="doctors-all-link" className="text-sm font-semibold text-[var(--accent)] hover:underline">
            {t({ tr: "Tümünü gör →", en: "See all →" })}
          </Link>
        </div>
        <div className="grid md:grid-cols-3 gap-6 mt-12">
          {DOCTORS.map((d) => (
            <div key={d.id} className="rounded-xl glass-card p-7 hover:shadow-lg transition-shadow" data-testid={`doctor-card-${d.id}`}>
              <div className="w-14 h-14 rounded-full bg-[var(--accent-soft)] text-[var(--accent)] font-heading font-bold flex items-center justify-center text-lg mb-5">
                {d.name.split(" ").slice(-2).map((w) => w[0]).join("")}
              </div>
              <h3 className="font-heading font-semibold text-[#F2F5F7]">{d.name}</h3>
              <p className="text-sm text-[var(--accent)] font-medium mt-0.5">{t(d.title)}</p>
              <p className="text-xs text-[#94A3AE] mt-2">{d.diploma}</p>
              <p className="text-sm text-[#94A3AE] mt-3 leading-relaxed">{t(d.focus)}</p>
              <p className="text-xs text-[#94A3AE] mt-3">{d.years} {t({ tr: "yıl deneyim", en: "years of experience" })} · {d.langs.join(" / ")}</p>
            </div>
          ))}
        </div>
      </MotionSection>

      {/* TESTIMONIALS */}
      <MotionSection className="bg-[#0A0E14] border-y border-white/10" data-testid="testimonials-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-24">
          <SectionTag>{t({ tr: "Hasta deneyimleri", en: "Patient experiences" })}</SectionTag>
          <h2 className="font-heading text-2xl md:text-3xl font-semibold text-[#F2F5F7] tracking-tight">
            {t({ tr: "İzinli, yazılı deneyimler", en: "Consented, written experiences" })}
          </h2>
          <div className="grid md:grid-cols-3 gap-6 mt-12">
            {TESTIMONIALS.map((x, i) => (
              <blockquote key={i} className="rounded-xl bg-white/[0.04] p-7 border border-white/10">
                <p className="text-sm text-[#94A3AE] leading-relaxed">"{t(x.text)}"</p>
                <footer className="mt-4 text-xs font-semibold text-[#F2F5F7]">
                  {x.name} · <span className="text-[#94A3AE] font-normal">{t(x.place)}</span>
                </footer>
              </blockquote>
            ))}
          </div>
        </div>
      </MotionSection>

      <div className="mirror-divider max-w-6xl mx-auto" aria-hidden="true" />

      {/* JOURNEY */}
      <MotionSection className="max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-24" data-testid="journey-section">
        <div className="rounded-2xl overflow-hidden grid lg:grid-cols-2 bg-[#0A0E14]">
          <img src={ASSETS.clinic} alt={t({ tr: "Klinik ortamı", en: "Clinic environment" })} className="w-full h-64 lg:h-full object-cover" loading="lazy" />
          <div className="p-10 md:p-14 text-white">
            <SectionTag>{t({ tr: "Yolculuğunuz", en: "Your journey" })}</SectionTag>
            <h2 className="font-heading text-2xl md:text-3xl font-semibold tracking-tight">
              {t({ tr: "Seyahat, konaklama ve transfer planlanır", en: "Travel, accommodation and transfers are planned" })}
            </h2>
            <p className="mt-4 text-white/70 leading-relaxed">
              {t({
                tr: "Havaalanı karşılamasından otel seçimine, klinik transferlerinden tedavi sonrası takibe kadar tüm süreç koordinatörünüz tarafından yönetilir.",
                en: "From airport pickup to hotel selection, clinic transfers to aftercare, the whole process is managed by your coordinator.",
              })}
            </p>
            <div className="mt-7">
              <CtaLink to="/yolculuk" testId="journey-cta" variant="ghost">
                {t({ tr: "Hasta yolculuğunu incele", en: "Explore the patient journey" })}
              </CtaLink>
            </div>
          </div>
        </div>
      </MotionSection>

      {/* FAQ SHORT */}
      <MotionSection className="max-w-4xl mx-auto px-6 md:px-12 py-16 md:py-20" data-testid="faq-short-section">
        <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
          <h2 className="font-heading text-2xl md:text-3xl font-semibold text-[#F2F5F7] tracking-tight">
            {t({ tr: "Sık sorulan sorular", en: "Frequently asked questions" })}
          </h2>
          <Link to="/sss" data-testid="faq-all-link" className="text-sm font-semibold text-[var(--accent)] hover:underline">
            {t({ tr: "Tümü →", en: "All →" })}
          </Link>
        </div>
        <Accordion type="single" collapsible className="space-y-3">
          {FAQS.slice(0, 5).map((f, i) => (
            <AccordionItem key={i} value={`f${i}`} data-testid={`faq-item-${i}`} className="rounded-xl glass-card px-6">
              <AccordionTrigger className="text-left text-sm font-semibold text-[#F2F5F7] hover:no-underline">{t(f.q)}</AccordionTrigger>
              <AccordionContent className="text-sm text-[#94A3AE] leading-relaxed">{t(f.a)}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </MotionSection>

      {/* CLOSING CTA */}
      <MotionSection className="max-w-7xl mx-auto px-6 md:px-12 pb-4" data-testid="closing-cta">
        <div className="rounded-2xl hero-mirror border border-white/10 px-8 md:px-16 py-16 text-center">
          <h2 className="font-heading text-2xl md:text-3xl font-semibold text-[#F2F5F7] tracking-tight">
            {t({ tr: "Kendin için hangi dönüşümü seçersin?", en: "Which transformation would you choose for yourself?" })}
          </h2>
          <p className="mt-3 text-[#94A3AE]">{t({ tr: "ZETA Mirror ile başlayın, koordinatörümüz 24 saat içinde dönsün.", en: "Start with ZETA Mirror; our coordinator responds within 24 hours." })}</p>
          <div className="mt-8 flex justify-center">
            <CtaLink to="/mirror" testId="closing-mirror-cta" onClick={() => postEvent("mirror_started", { from: "closing" })}>
              <Sparkles size={16} /> {t({ tr: "ZETA Mirror'ı Başlat", en: "Start ZETA Mirror" })}
            </CtaLink>
          </div>
        </div>
      </MotionSection>
    </main>
  );
}
